/**
 * @file Text atom — bound to the ratified type scale (RFC §3).
 * @description The only public text primitive. `variant` applies a bundled
 * text style (size + line-height + family/weight). className stays open for
 * color/layout tokens; the vocabulary is closed by theme + lint.
 * @module components/atoms/Text
 */
import React from "react";

import { Text as UIText } from "@/components/ui/text";

import {
  TEXT_VARIANT_CLASS,
  type TextVariant,
  type WithUnsafeStyle,
} from "../types";

/**
 *
 */
type UITextProps = React.ComponentProps<typeof UIText>;

/**
 * A ratified text style, or `"unstyled"` — the explicit opt-out (SE-8566) for
 * sites whose rendered size + face has no ratified style (16 Regular, 12
 * Medium, …). It applies no bundle, so the element renders exactly what its
 * className says on top of the vendored base (16/24 Satoshi Regular). Every
 * app-code use is listed, with a reason, by the text-style lint gate
 * (eslint.text-style.ts) and in docs/design-system/typography-inventory.md.
 */
export type TextAtomVariant = TextVariant | "unstyled";

/**
 *
 */
export interface TextProps
  extends
    Omit<UITextProps, "size" | "style">,
    WithUnsafeStyle<UITextProps["style"]> {
  /** Ratified text style bundle, or the reviewed `"unstyled"` opt-out. */
  variant: TextAtomVariant;
  /** Test/a11y identity is part of the atom API (RFC standing constraint 4). */
  testID?: string;
  /**
   * Truncate after this many lines (an integer). Forwarded to the underlying
   * text. Pair with `ellipsizeMode` to control where the ellipsis lands.
   */
  numberOfLines?: number;
  /**
   * Where the ellipsis is placed when text is truncated — RN's fixed 4-member
   * enum. A closed enum, not a free style value.
   */
  ellipsizeMode?: "head" | "middle" | "tail" | "clip";
}

const Text = React.forwardRef<React.ComponentRef<typeof UIText>, TextProps>(
  function Text({ variant, className, UNSAFE_style, ...props }, ref) {
    const bundle =
      variant === "unstyled" ? undefined : TEXT_VARIANT_CLASS[variant];
    const { testID } = props;
    return (
      <UIText
        ref={ref}
        className={bundle ? `${bundle} ${className ?? ""}`.trim() : className}
        style={UNSAFE_style}
        // web Text renders a raw span; emit data-testid for the e2e contract
        {...(testID ? ({ "data-testid": testID } as object) : {})}
        {...props}
      />
    );
  }
);

Text.displayName = "Text";

export { Text };
